import { cn } from "@/utils/cn"
import { Loader2 } from "lucide-react"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"

interface LoadingStateProps {
  type?: 'spinner' | 'skeleton' | 'dots' | 'progress'
  message?: string
  progress?: number
  rows?: number
  className?: string
}

export const LoadingState = ({
  type = 'spinner',
  message,
  progress,
  rows = 3,
  className
}: LoadingStateProps) => {
  if (type === 'skeleton') {
    return (
      <div className={cn("space-y-3", className)} role="status" aria-label="Loading content">
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-4 w-full" />
            {index === 0 && <Skeleton className="h-4 w-3/4" />}
            {index === 1 && <Skeleton className="h-4 w-1/2" />}
          </div>
        ))}
      </div>
    )
  }

  if (type === 'dots') {
    return (
      <div
        className={cn("flex items-center justify-center space-x-1", className)}
        role="status"
        aria-label="Loading"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 bg-primary rounded-full animate-bounce"
            style={{
              animationDelay: `${i * 0.1}s`,
              animationDuration: '0.6s'
            }}
          />
        ))}
        {message && (
          <span className="ml-2 text-sm text-muted-foreground">
            {message}
          </span>
        )}
      </div>
    )
  }

  if (type === 'progress' && typeof progress === 'number') {
    return (
      <div
        className={cn("flex flex-col items-center justify-center space-y-4", className)}
        role="status"
        aria-label="Loading progress"
      >
        <Progress value={progress} className="w-full max-w-sm" />
        <div className="text-center">
          <div className="text-2xl font-semibold text-foreground">
            {Math.round(progress)}%
          </div>
          {message && (
            <p className="text-sm text-muted-foreground mt-1">
              {message}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Default spinner
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center space-y-4 p-8",
        className
      )}
      role="status"
      aria-label="Loading"
    >
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      {message && (
        <p className="text-sm text-muted-foreground text-center max-w-sm">
          {message}
        </p>
      )}
    </div>
  )
}